// @ts-nocheck

import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { XMasBoostedButtonSnowBgImgPath } from '../../assets/img';
import { Target } from '../../client';
import NumberFormatter from '../../i18n/numberformatter';
import theme from '../../styled-components/dark';
import { CardModes, type CardMode } from '../../types/common';
import { type OutcomeMode } from '../../types/outcome';
import { OutcomeModes } from '../../types/outcome';
import OddUpdateIndicator from '../OddUpdateIndicator';

const Wrapper = styled.div`
  position: relative;
  box-sizing: border-box;
  font-size: 1.15em;

  height: 100%;
  border-radius: 0.5em;
  transition: all 200ms ease-in-out;
  padding: 0.25em;

  -webkit-tap-highlight-color: transparent;

  user-select: none;
  cursor: ${({ $mode }) => ($mode === OutcomeModes.PLAY ? 'pointer' : 'auto')};
  pointer-events: ${({ $isNewValue }) => (!$isNewValue ? 'none' : undefined)};
  line-height: 1;

  ${({ $isNewValue, $oldValue }) => {
    if ($isNewValue && $oldValue) {
      return css`
        width: 65%;
        margin: 0 0 0 0.4em;
      `;
    }
    if (!$isNewValue) {
      return css`
        width: 33%;
        margin: 0 0.4em 0 0;
      `;
    }
    return css`
      width: 100%;
      margin: 0;
    `;
  }};

  ${({ $isNewValue, $selected, $cardMode }) => css`
    ${$isNewValue
      ? css`
          background: ${`${
              $cardMode === CardModes.XMAS_BOOSTED
                ? `url(${XMasBoostedButtonSnowBgImgPath}) no-repeat 0% 0% / cover, `
                : ''
            }`}
            ${theme.OddButton[$cardMode]?.backgroundGradiant} no-repeat 0% 0% / 300%;
          transform-style: preserve-3d;

          ${!Target.__MOBILE__
            ? css`
                &:hover {
                  filter: brightness(110%);
                }
              `
            : undefined}

          ${$selected
            ? css`
                background-position: ${$cardMode === CardModes.XMAS_BOOSTED ? `100% 0%, ` : ''}100% 0%;
                box-shadow: 0 0 0 1pt ${theme.OddButton.active.backgroundColor};

                &:after {
                  opacity: 1;
                }
              `
            : css`
                box-shadow: 0 0 0 1pt ${theme.OddButton[$cardMode]?.borderColor};

                &:after {
                  opacity: 0;
                }
              `}
        `
      : css`
          background-color: ${theme.OddButton[$cardMode]?.oldValueBackground};
          box-shadow: 0 0 0 1pt ${theme.OddButton[$cardMode]?.oldValueBackground};
        `}
  `};
`;

const AbsoluteDiv = styled.div`
  position: absolute;

  width: 1em;
  height: 1.3em;
  top: 0.5em;
  right: -0.5em;
  z-index: 4;

  ${({ $cardMode }) => css`
    background: ${theme.OddButton[$cardMode]?.oldValueArrowBackground};
  `}
`;

const AbsoluteDivArrow = styled.div`
  border-bottom: 1.3em solid transparent;
  position: absolute;
  width: 0%;
  height: 0em;
  top: -0.1em;
  right: -1.5em;
  z-index: 6;
  border-top: 1.3em solid transparent;

  ${({ $cardMode }) => css`
    border-left: 1.4em solid ${theme.OddButton[$cardMode]?.oldValueArrowBackground};
  `}

  transform: scaleX(0.7) scaleY(0.9);
`;

const ValueWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Value = styled.span`
  position: relative;
  text-align: center;
  color: ${({ $isNewValue, $cardMode }) => ($isNewValue ? theme.OddButton[$cardMode]?.valueColor : '#525c7a')};
  font-weight: bold;
  font-size: 1.8em;

  ${({ $isNewValue }) =>
    !$isNewValue
      ? css`
          transform: scale(0.8);
        `
      : undefined}
`;

const StyledOddUpdateIndicator = styled(OddUpdateIndicator)`
  position: absolute;
  right: 0;
  bottom: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Center = styled.div`
  position: absolute;
  pointer-events: none;
  background: transparent;
  left: 50%;
  top: 50%;
`;

type PropsType = {
  value: number;
  mode?: OutcomeMode;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
  isNewValue?: boolean;
  cardMode?: CardMode;
  oldValue?: number;
  oddId?: number;
  uuid?: string;
};

const BoostedOddButton = ({
  value,
  mode = OutcomeModes.PLAY,
  selected = false,
  onClick = () => {},
  className,
  isNewValue = false,
  cardMode,
  oldValue,
  oddId,
  uuid = '',
}: PropsType) => {
  const displayedValue = useMemo(() => {
    if (value && value > 0 && mode !== OutcomeModes.STOP) {
      return NumberFormatter.formatOdd(value);
    }
    return '-';
  }, [mode, value]);

  return (
    <Wrapper
      className={className}
      onClick={onClick}
      $oldValue={oldValue}
      $isNewValue={isNewValue}
      $selected={selected}
      $mode={mode}
      $cardMode={cardMode}
    >
      {oddId ? <Center id={oddId ? `odd-button-${oddId}-${uuid}` : null} /> : null}
      {!isNewValue && <AbsoluteDiv $cardMode={cardMode} />}
      {!isNewValue && <AbsoluteDivArrow $cardMode={cardMode} />}
      <ValueWrapper>
        <Value $cardMode={cardMode} $isNewValue={isNewValue}>
          {displayedValue}
        </Value>
        <StyledOddUpdateIndicator value={value} isWhite={selected} />
      </ValueWrapper>
    </Wrapper>
  );
};

export default BoostedOddButton;
